import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { connect } from 'react-redux';
import { Col, Row, Container, Button, Form, Modal } from "react-bootstrap";
import RefreshIcon from '@mui/icons-material/Refresh';
import styled from 'styled-components';

import constants from "../../const";
import PositionCard from "./position-card.js";
import TimelineComponent from "../common/TimelineComponent.js";
import PositionSelector from "../common/PositionSelector";

function TimelineEditorWindow(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const sceneIdFromUrl = urlParams.get('sceneId');

  // Get data from sessionStorage
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sceneId, setSceneId] = useState(sceneIdFromUrl);

  const [allPositions, setAllPositions] = useState([]);
  const [allPositionTags, setAllPositionTags] = useState([]);
  const [scenePositions, setScenePositions] = useState([]);
  const [positionBar, setPositionBar] = useState([]);

  const [actions, setActions] = useState([]);
  const [timelineCurrentTime, setTimelineCurrentTime] = useState(0);
  const [timelinePlaying, setTimelinePlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoTime, setVideoTime] = useState(0);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const syncFromTimelineRef = useRef(false);
  const vidRef = useRef(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showQuickPositionSelector, setShowQuickPositionSelector] = useState(false);
  const [newPositionForm, setNewPositionForm] = useState({
    position: '',
    startTime: 0,
    endTime: 0
  });

  useEffect(() => {
    const storedData = sessionStorage.getItem('timelineEditorData');
    console.log('Timeline editor window - stored data:', storedData);
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        console.log('Parsed data:', data);
        setSessionData(data);
        setVideoDuration(data.videoDuration || 0);
        setAllPositions(data.allPositions || []);
        setScenePositions(data.scenePositions || []);
        setSceneId(data.sceneId);
        // Transform positions to actions
        if (data.scenePositions && data.scenePositions.length > 0) {
          const transformedActions = data.scenePositions.map(pos => ({
            id: pos.id.toString(),
            type: `P${pos.position}`,
            name: pos.name,
            start: pos.startTime,
            end: pos.endTime === 0 ? (data.videoDuration || 180) : pos.endTime
          }));
          setActions(transformedActions);
          console.log('Transformed actions:', transformedActions);
        }
      } catch (e) {
        console.log('Could not parse session data:', e);
      }
    } else if (sceneIdFromUrl) {
      setSceneId(sceneIdFromUrl);
      console.log('Using sceneId from URL:', sceneIdFromUrl);
    }
    setIsLoading(false);
  }, []);

  const insertScenePosition = async (position) => {
    await axios.post(`${constants.DATA1_API_URL}/scene/${sceneId}/positions`, position, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });
    fetchScenePositions();
  };

  const updateScenePosition = async (position) => {
    await axios.put(`${constants.DATA1_API_URL}/scene/${sceneId}/positions`, position, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });
    fetchScenePositions();
  };

  const removeScenePosition = async (position) => {
    await axios.delete(`${constants.DATA1_API_URL}/scene/${sceneId}/positions/${position.id}`, {
      headers: { 'Authorization': `${localStorage.getItem('token')}` }
    });
    fetchScenePositions();
  };

  const fetchScenePositions = () => {
    console.log(`Fetching positions from: ${constants.DATA1_API_URL}/scene/${sceneId}/positions`);
    axios.get(`${constants.DATA1_API_URL}/scene/${sceneId}/positions`).then((res) => {
      console.log('Fetched scene positions:', res.data);
      setScenePositions(res.data);
      positionTimeline(res.data);
      setActions(transformPositionsToActions(res.data, videoDuration || 180));
    }).catch((err) => {
      console.error('Error fetching scene positions:', err);
    });
  };

  const fetchPositions = () => {
    axios.get(`${constants.DATA1_API_URL}/scenes/positions`).then((res) => {
      console.log('Fetched all positions:', res.data);
      setAllPositions(res.data);
    }).catch((err) => {
      console.error('Error fetching positions:', err);
    });
  };

  const fetchPositionCategory = () => {
    axios.get(`${constants.DATA1_API_URL}/scenes/tag-position`).then((res) => {
      console.log('Fetched position categories:', res.data);
      setAllPositionTags(res.data);
    }).catch((err) => {
      console.error('Error fetching position categories:', err);
    });
  };

  const positionTimeline = (positions) => {
    const total = Math.ceil(180); // Default duration
    const timePer = (time, total) => {
      return Math.ceil(time / total * 100);
    };
    const data = [];
    for (let i = 0; i < positions.length; i++) {
      const start = timePer(positions[i]?.startTime, total);
      const end = timePer(positions[i]?.endTime, total);
      const dur = end - start;
      if (i === 0) {
        const space = timePer(positions[i + 1]?.startTime, total) - 0;
        data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space });
      } else if (i < positions.length - 1) {
        const space = timePer(positions[i + 1]?.startTime, total) - timePer(positions[i]?.endTime, total);
        if (space > 0) {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur });
          data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space });
        } else {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur });
        }
      } else {
        const space = 100 - timePer(positions[i]?.endTime, total);
        if (space > 0) {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur });
          data.push({ type: 'Not Covered', start: timePer(positions[i]?.endTime, total), dur: space });
        } else {
          data.push({ type: 'Covered', start: timePer(positions[i]?.startTime, total), dur: dur });
        }
      }
    }
    setPositionBar(data);
  };

  const selectPosition = (position) => {
    insertScenePosition({
      scene: sceneId,
      position: position.position || position.id,
      startTime: 0,
      endTime: 0
    });
    setShowQuickPositionSelector(false);
  };

  const transformPositionsToActions = (positions, duration) => {
    return positions.map(pos => ({
      id: pos.id.toString(),
      type: `P${pos.position}`,
      name: pos.name,
      start: pos.startTime,
      end: pos.endTime === 0 ? (duration || 180) : pos.endTime
    }));
  };

  useEffect(() => {
    if (scenePositions.length > 0) {
      setActions(transformPositionsToActions(scenePositions, videoDuration || 180));
    }
  }, [videoDuration]);

  const handleActionChange = (updatedActions) => {
    setActions(updatedActions);
    
    updatedActions.forEach(action => {
      const positionId = parseInt(action.id);
      const position = scenePositions.find(p => p.id === positionId);
      
      if (position && (position.startTime !== action.start || position.endTime !== action.end)) {
        updateScenePosition({
          id: position.id,
          scene: position.scene,
          position: position.position,
          name: position.name,
          startTime: action.start,
          endTime: action.end
        });
      }
    });
  };

  const handleTimelinePlayingChange = (isPlaying) => {
    setTimelinePlaying(isPlaying);
    if (vidRef.current) {
      if (isPlaying) {
        vidRef.current.play().catch(err => console.log('Play error:', err));
      } else {
        vidRef.current.pause();
      }
    }
  };

  const handleTimelineTimeChange = (newTime) => {
    setTimelineCurrentTime(newTime);
    syncFromTimelineRef.current = true;
    if (vidRef.current) {
      vidRef.current.currentTime = newTime;
    }
    setTimeout(() => {
      syncFromTimelineRef.current = false;
    }, 50);
  };

  const handleVideoTimeUpdate = () => {
    if (vidRef.current && !syncFromTimelineRef.current) {
      setTimelineCurrentTime(vidRef.current.currentTime);
      setVideoTime(vidRef.current.currentTime);
    }
  };

  const handleVideoPlayPause = () => {
    if (vidRef.current && !syncFromTimelineRef.current) {
      setTimelinePlaying(!vidRef.current.paused);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (vidRef.current) {
      setVideoDuration(vidRef.current.duration);
    }
  };

  const handleAddNewPosition = () => {
    setNewPositionForm({
      position: '',
      startTime: Math.round(timelineCurrentTime),
      endTime: Math.round(timelineCurrentTime + 5)
    });
    setShowAddModal(true);
  };

  const handleSaveNewPosition = async () => {
    if (!newPositionForm.position) {
      alert('Please select a position type');
      return;
    }

    const selectedPosType = allPositions.find(p => p.id === parseInt(newPositionForm.position));
    
    await insertScenePosition({
      scene: sceneId,
      position: parseInt(newPositionForm.position),
      name: selectedPosType?.name || 'Position',
      startTime: Math.round(newPositionForm.startTime),
      endTime: Math.round(newPositionForm.endTime)
    });
    
    setShowAddModal(false);
  };

  useEffect(() => {
    console.log('Fetching positions for sceneId:', sceneId);
    fetchPositions();
    fetchPositionCategory();
    if (sceneId) {
      fetchScenePositions();
      console.log('Fetching scene positions for:', sceneId);
    }
  }, [sceneId]);

  if (isLoading) {
    return (
      <Container fluid style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Loading Timeline Editor...</h3>
      </Container>
    );
  }

  if (!sceneId) {
    return (
      <Container fluid style={{ padding: '20px', textAlign: 'center' }}>
        <h3 style={{ color: 'red' }}>Error: Scene ID not found. Please try again.</h3>
      </Container>
    );
  }

  const videoUrl = sessionData?.videoUrl || '';
  console.log('Rendering timeline with - sceneId:', sceneId, 'videoUrl:', videoUrl, 'videoDuration:', videoDuration, 'scenePositions:', scenePositions);

  return (
    <>
      <Container fluid>
        <Row style={{ marginBottom: '20px' }}>
          <Col md="12">
            <VideoContainer>
              <video ref={vidRef} id={`video-${sceneId}`} controlsList="nodownload" playsInline={true}
                onTimeUpdate={handleVideoTimeUpdate}
                onPlay={handleVideoPlayPause}
                onPause={handleVideoPlayPause}
                onLoadedMetadata={handleVideoLoadedMetadata}
                controls style={{ width: "100%", height: "300px" }}>
                <source src={videoUrl} type="video/mp4" />
              </video>
            </VideoContainer>
          </Col>
        </Row>

        {props.perms && Array.isArray(props.perms) && props.perms.some(e => e && e.role && e.role.id === 2) ? <Row className="m-1">
          <Button className="btn btn-sm btn-light" onClick={() => setShowQuickPositionSelector(true)}>
            Quick Add Position
          </Button>
          <Button className={`btn btn-sm btn-light`} onClick={fetchScenePositions}><RefreshIcon /></Button>
          <Col>
            <div className="progress">
              {positionBar.map((d,idx) => {
                return <div key={`progress-bar-${d.type}-${idx}`} className={`progress-bar ${d.type == 'Covered' ? 'bg-success' : 'bg-secondary'}`} role="progressbar" style={{ width: `${d.dur}%` }} aria-valuenow={d.dur} aria-valuemin="0" aria-valuemax="100">|</div>
              })}
            </div>
          </Col>

          {/* Quick Position Selector */}
          <PositionSelector
            show={showQuickPositionSelector}
            onClose={() => setShowQuickPositionSelector(false)}
            onSelect={selectPosition}
            positions={allPositions}
            selected={null}
            title="Quick Add Positions"
            buttonText="Add Position"
            searchPlaceholder="🔍 Search positions..."
            groupByCategory={true}
            categoryField="category"
            searchField="name"
            filterCategory="1Main"
          />
        </Row> : null}


        <Row style={{ marginBottom: '20px' }}>
          <Col md="12">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '10px' }}>
              <h5 style={{ margin: 0 }}>Video Timeline</h5>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => setTimelineCollapsed(!timelineCollapsed)}
              >
                {timelineCollapsed ? '▶ Expand' : '▼ Collapse'}
              </Button>
            </div>
            {!timelineCollapsed && (
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                padding: '20px',
                backgroundColor: '#0a0a0a',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,.1)'
              }}>
                <div style={{ transform: 'scale(1.2)', transformOrigin: 'top left', width: '120%' }}>
                  <TimelineComponent
                    durationSec={videoDuration || 180}
                    actions={actions}
                    readOnly={false}
                    onActionChange={handleActionChange}
                    title="Video Timeline"
                    currentTime={timelineCurrentTime}
                    playing={timelinePlaying}
                    onPlayingChange={handleTimelinePlayingChange}
                    onTimeChange={handleTimelineTimeChange}
                  />
                </div>
                <Button 
                  variant="success" 
                  size="sm" 
                  onClick={handleAddNewPosition}
                >
                  + Add Position at {Math.round(timelineCurrentTime)}s
                </Button>
              </div>
            )}
          </Col>
        </Row>

        <Row style={{ marginBottom: '30px' }}>
          {scenePositions.map((pos) =>
            <PositionCard key={`card-${pos.id}`}
              style={{ width: "200px" }}
              position={pos}
              positionList={allPositions || []}
              positionTagList={allPositionTags || []}
              roles={props.perms || []}
              refresh={fetchScenePositions}
              update={updateScenePosition}
              remove={removeScenePosition}
              jumpVideo={handleTimelineTimeChange} />
          )}
        </Row>

        <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Position</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Position Type</Form.Label>
              <Form.Select 
                value={newPositionForm.position}
                onChange={(e) => setNewPositionForm({...newPositionForm, position: e.target.value})}
              >
                <option value="">Select position type...</option>
                {allPositions.map(pos => (
                  <option key={pos.id} value={pos.id}>
                    {pos.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Start Time (seconds)</Form.Label>
              <Form.Control 
                type="number" 
                value={newPositionForm.startTime}
                onChange={(e) => setNewPositionForm({...newPositionForm, startTime: parseFloat(e.target.value)})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>End Time (seconds)</Form.Label>
              <Form.Control 
                type="number" 
                value={newPositionForm.endTime}
                onChange={(e) => setNewPositionForm({...newPositionForm, endTime: parseFloat(e.target.value)})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSaveNewPosition}>
              Save Position
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

const VideoContainer = styled.div`
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  margin: 8px 0;

  video {
    width: 100%;
    display: block;
  }
`;

const mapStateToProps = (state) => {
  return {
    perms: state && state.users && state.users.permissions && state.users.permissions.perms ? state.users.permissions.perms : []
  };
};

export default connect(mapStateToProps)(TimelineEditorWindow);
